import { useState, useEffect } from 'react';
import { useAccountant } from '../../contexts/AccountantContext';
import UserGuideQRCard from '../../components/UserGuideQRCard';
import { APP_REVISION } from '../../lib/appRevision';
import { 
  Book, 
  House,
  Users,
  CurrencyDollar,
  ChartLineUp,
  Warehouse,
  FileText,
  CalendarCheck,
  ShieldCheck,
  Gear,
  Buildings,
  Receipt,
  ClipboardText,
  CheckCircle,
  CaretRight,
  MagnifyingGlass,
  Briefcase,
  Handshake,
  Lightning,
  Target,
  Rocket,
  ArrowRight,
  Star
} from '@phosphor-icons/react';

export default function UserGuidePage() {
  const { accountantMode } = useAccountant();
  const [activeSection, setActiveSection] = useState('getting-started');
  const [searchQuery, setSearchQuery] = useState('');

  // If the user lands here from the topbar Help button (URL hash like
  // #end-to-end-workflow), jump straight to that section.
  useEffect(() => {
    const hash = (typeof window !== 'undefined' ? window.location.hash : '') || '';
    if (hash) {
      const id = hash.replace(/^#/, '');
      if (id) setActiveSection(id);
    }
  }, []);

  // Standard BizCore Guide Sections
  const standardSections = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: Rocket,
      content: [
        {
          title: 'Welcome to BIZCore™',
          description: 'BIZCore™ is your all-in-one ERP solution designed for SMEs. This guide will help you get started with managing your business operations efficiently.',
          steps: [
            'Register your account and company',
            'Set up your company profile and regional settings',
            'Configure your chart of accounts',
            'Add employees (departments & designations can be created inline)',
            'Add customers and suppliers (can be created inline when needed)',
            'Start creating invoices and quotations'
          ]
        },
        {
          title: 'Quick Start Tips',
          description: 'BIZCore lets you create related items on-the-fly:',
          items: [
            { label: 'Adding Employee?', desc: 'Click + button next to Department/Designation to create new ones inline' },
            { label: 'Creating Invoice?', desc: 'Click + button next to Customer to add a new customer without leaving the form' },
            { label: 'Creating PO?', desc: 'Click + button next to Supplier to add a new supplier inline' }
          ]
        },
        {
          title: "What's New (Latest Update)",
          description: 'Recent additions you should explore:',
          items: [
            { label: 'Part Invoicing per Quotation (NEW)', desc: 'Sales → Quotations → row menu → "Create Part Invoice…". Bill a quotation in slices with a per-line Approved % + Apply mode (Amount scales the total, Qty scales the quantity). Cumulative-% enforced ≤ 100 per line. All parts share the same base number with a numeric suffix (INV-0025-1, -2, -3 …). Advance vouchers on the quotation appear in a pool and can be applied per invoice. When every line reaches 100 %, the quotation flips to status = invoiced.' },
            { label: 'Part Invoicing per Project (NEW)', desc: 'Invoices tab → "New Part Invoice (Project)" button. Chain identified by customer + Project Reference. Round 1 mints the base scope — you manually enter Original Qty + Original Rate + Approved Qty per line. Round 2+ auto-loads the anchor scope and only allows billing up to each line\'s balance qty. Approved Qty ↔ Approved % stay in sync. Editing Original Qty/Rate on a later round is allowed but the deviation is captured on the invoice audit trail. Fully autosaved to your browser with Restore-draft on reopen.' },
            { label: 'Part Invoice → Accounting Wiring', desc: 'Part invoices appear on GET /invoices with is_part_invoice + part_sequence + base_invoice_number metadata. Approval creates a balanced Journal Entry (Debit AR total, Credit Sales subtotal + Tax payable). Shows on the customer Statement of Account with correct debit / running balance. Same JE + SoA path as any regular invoice — no double bookkeeping.' },
            { label: 'Finance Reconciliation & Auto-Heal (NEW)', desc: 'A new Dashboard banner detects drift between invoices, receipt vouchers and the ledger — caused by manually deleting duplicate vouchers or partial payments. Click "Reconcile Now" and the system: (a) recomputes every invoice\'s amount_paid / balance_due / payment_status from its ACTIVE Receipt Vouchers, (b) reverts paid invoices whose receipt voucher was deleted back to "Approved" so you can re-record the payment to the correct bank account, (c) posts any missing Journal Entries for approved invoices (DR AR / CR Revenue) and receipts (DR Bank / CR AR), (d) voids JEs left orphaned by deleted vouchers. Idempotent and admin-only.' },
            { label: 'Mark Invoice as Paid — Bank Account Picker (NEW)', desc: 'When you click "Mark as Paid" on an invoice, a dialog now asks for the payment date, payment mode (Cash / Bank Transfer / Cheque / Card / Online) and the EXACT bank or cash account that received the money. The auto-created Receipt Voucher posts the journal entry to that account — so the deposit shows up in your Bank Statement, Trial Balance, P&L and Dashboard immediately.' },
            { label: 'Financial Reports — 3 Tabs in One Place', desc: 'Finance & Accounts → Reports now ships with three tabs side-by-side: Profit & Loss, Balance Sheet, Trial Balance. Each tab has its own date picker, in-balance badge and one-click PDF / Excel / Print export from the same toolbar.' },
            { label: 'AMC / Agreements PDF — Cleaner Format', desc: 'Section headings are now dark text on white background (was unreadable dark-on-dark on pages 6-8). Markdown "---" horizontal-rule separators removed. AI honours the agreement type exactly — Non-Comprehensive agreements no longer get mislabeled as Comprehensive. Added standard clauses: PROJECT MANAGEMENT & SUPERVISION, UAE-only Governing Law & Jurisdiction, "No insurance claims or fines whatsoever" disclaimer, Force Majeure (sandstorm / DEWA outage / pandemic), Sub-Contractors, and an AI-suggested Exclusions list.' },
            { label: 'PDF Footer & Title Polish (across ALL documents)', desc: 'Every document PDF (Invoice, Quotation, Report, Job Order, Service Report, Agreement) now has a uniform clean 3-cell footer (Company Name · Page X of Y · Document Number) that never wraps to a second line. Document titles are now a uniform 18pt across all templates so reports look consistent next to invoices.' },
            { label: 'Dashboard Re-sync Now no longer freezes the page', desc: 'The Accounting Health "Re-sync now" button now auto-cleans legacy bad vouchers (assigns default Cash account when missing, soft-deletes zero-amount vouchers), continues on partial failures, and reports a precise toast of healed / cleaned / failed counts. No more silent React crash.' },
            { label: 'Agreements Module (NEW)', desc: 'Operations → Agreements. Full AMC / Service Agreement / One-time Contract / SLA module with 7 agreement types (Comprehensive/Non-Comprehensive AMC, Quarterly, Half-Yearly, One-time, Project, SLA), 7 trades (HVAC/Electrical/Plumbing/Fire-fighting/BMS/Generator/Solar), AI-drafted Preamble + Scope + Major/Minor Checklist + T&C + Termination Clause (Gemini), pre-seeded checklist templates per trade, BOQ table, Payment Schedule (Annual/Half-yearly/Quarterly/Monthly/Custom), automatic instalment generation, one-click "Generate next invoice" from any payment term, Duplicate, Renew (with escalation %), branded multi-layout PDF, Signature blocks for both Contractor + Client with Name + Position, Search + Status/Type/Trade/Expiring-soon filters.' },
            { label: 'Service Reports Module', desc: 'Inside any saved Job Order → 4th tab "Service Reports". Site team fills the work-completion document AFTER attending the job: tickable checklist (pre-filled from the Job Order), work-done narrative, materials used (name + qty + unit), people who attended (name + role), before/after photos, comments, optional next-visit date+reason, and a customer-rep signature captured on a touch canvas. Branded PDF with company LOGO downloadable for on-site handover. Status: Draft → Submitted → Approved.' },
            { label: 'Customer Dropdown with Inline Add/Edit', desc: 'Job Order form now has a searchable Customer combobox pulled live from your Contacts. Type to filter. Click + to add a brand-new customer inline (Name, TRN, Phone, Email, Address, Company name) without leaving the Job Order. Click the pencil to edit the selected customer.' },
            { label: 'Job Order WhatsApp Share + Save & Download PDF', desc: 'Each row on the Job Orders list now has a WhatsApp button — opens wa.me with a pre-filled message (JO#, Customer, Site, Equipment, Status). The form also has a one-click "Save & Download PDF" that saves and immediately downloads the branded PDF so you can send it to site instantly.' },
            { label: 'Job Order PDF Now Branded with Company Logo', desc: 'Job Order and Service Report PDFs now include the same letterhead block as your Invoices/Quotations — company logo, TRN, address, phone, email, website — styled by the layout you pick (Classic / Modern Band / Minimalist / Compact).' },
            { label: 'Job Orders — 4-Tab Form for Better Discoverability', desc: 'New Job Order form is now organised into 4 clear tabs: 1. Details · 2. Checklist · 3. MoS & Risk (Optional) · 4. Service Reports. MoS and Risk Assessment are clearly marked "Optional" — skip them if not needed.' },
            { label: 'Job Orders Module', desc: 'Operations → Job Orders. Field/site work-orders for one-time service or PPM with: equipment multi-select (AHU, FAHU, FCU, Pumps, Chillers, Generators, etc. + custom), AI-generated equipment checklists, AI-drafted Method of Statement and Risk Assessment (Gemini), before/after pictures, signatures, full status workflow (Draft → Assigned → In Progress → Completed by Site → Approved), link to invoice/LPO, and a branded multi-layout PDF.' },
            { label: 'Inventory v2 — Warehouses, Bin Cards, Multi-store Transfers', desc: 'Inventory now supports multiple warehouses/stores. Default "Main Store" is auto-created. Open the Warehouses tab to add Branch / Site / Van locations. Move stock between stores via the new Stock Transfer workflow. Each item shows a Bin Card with full stock-in/stock-out movement history per warehouse.' },
            { label: 'Quotation Revisions + Diff Viewer', desc: 'Sales → Quotations → kebab → "Save as Revision" creates QTN-#### (R1), (R2), … while keeping the original document number. Toggle "Show Revisions" in the list to see all historical versions. Click two revisions and "Compare" to open the visual Diff Viewer — added/removed/changed line items, total deltas, and header field changes are highlighted side-by-side.' },
            { label: 'Universal Report Toolbar — PDF / Excel / Date Range / Search', desc: 'Every Financial and Inventory report (Trial Balance, P&L, Balance Sheet, Account Ledger, Stock Summary, Bin Cards, Transfers, Job Orders…) now has the same toolbar at the top: a date range picker, a search filter, plus View, PDF and Excel buttons. Exports honour your current filter and produce branded styled output.' },
            { label: 'Platform Admin Password Reset Tool', desc: 'Platform Admin page now has a "Reset User Password" tool. Type the email, click Reset, copy the temporary password, and share it with the user. The action also clears any failed-login lockout and login_attempts so the user can sign in immediately, and forces a password change on next login.' },
            { label: 'PDF Page-Break & Amount-in-Words Fixes', desc: 'Totals block, signature stamps and footers no longer split across pages on long invoices/quotations. "Amount in Words" on every PDF is now computed from the Final Payable (after advance/retention deductions) — not from the gross total.' },
            { label: 'VAT Tax Invoice Summary', desc: 'Reports → VAT Invoice Summary. Editable quarterly summary of all tax invoices (S/N, Date, Client, TRN, Particulars, Invoice Amount, 5% VAT, Net Amount) with totals + Prepared/Verified/Approved signature blocks. Pick a Quarter+Year preset (or custom From/To range), click "Load Invoices", edit any cell, then download as branded PDF or styled Excel.' },
            { label: 'HR — Permanently Delete Employee', desc: 'HR → kebab menu on any employee row. Admins now see "Permanently Delete" (alongside the existing soft-delete "Terminate"). Permanently deletes the employee record and all uploaded documents. Blocked when payroll history exists, so the books stay clean. Requires typing "DELETE" to confirm.' },
            { label: 'Document Summary Bar', desc: 'Inline KPI bar above every Sales / Purchases / Vouchers / Inventory list — Count, Total Value, Outstanding, This Month, Approved, Pending. One-click PDF and Excel summary exports.' },
            { label: 'Invoice Type Selector (Tax / Proforma / Invoice)', desc: 'New "Invoice Type" dropdown on the Create/Edit Invoice form. Tax Invoice → standard VAT-registered tax document with QR. Proforma Invoice → amber "not a tax invoice" badge, no QR (use before agreement is signed). Invoice → no "TAX" wording for non-VAT cases. The PDF title, footer and badge all adapt automatically.' },
            { label: 'Retention Math Reorder', desc: 'Retention is now deducted BEFORE VAT (matches industry-standard contractor invoicing). Order: Subtotal → less Retention → Net Taxable → +VAT → Gross → less Advance Received → less Previously Paid → Current Payable. Each line is rendered on the PDF in mono font so the math is auditable.' },
            { label: 'Type-aware Report Layouts (6 new forms)', desc: 'The Report Type dropdown now unlocks purpose-built forms: Inspection Report (status badges), Daily Report (weather/manpower/work-today), Weekly & Monthly Reports (cumulative %, highlights, risks), RFI (raised-to + response panel), Incident Report (severity badge, root cause). All non-Completion types share a red "Action Required" tracker. Empty sections auto-hide on the PDF.' },
            { label: 'Inline Report Attachments (any size)', desc: 'Attach JPG/PNG/GIF/WebP/PDF to any report — images embed inline as full pages, multi-page PDFs expand into multiple captioned pages, and the "Page X of Y" footer counts the attachment pages too. So a 1-page report + 2-page PDF + 1 image = "Page 1 of 4 → Page 4 of 4" all with the company footer.' },
            { label: 'Job Completion Report', desc: 'Pick "Job Completion Report" as the report type to unlock the full handover layout: Project Ref/Location, Client, LPO Ref, Work Details, editable Confirmation Statement, Client Signature pad (mouse + touch), Signatory Name + Date. Inline image attachments embed in the PDF; non-image files are listed in an "Attached Files" appendix.' },
            { label: 'Statement of Account (Receivables & Payables)', desc: 'Finance & Accounts → Statement of Accounts. Pick a customer (or supplier) + date range and instantly see Opening Balance → every Invoice/Receipt/Bill/Payment with running balance → Closing Balance, plus Aging buckets (Current / 1-30 / 31-60 / 61-90 / 90+). Export as branded PDF, Excel (.xlsx) or CSV. Shortcut: any contact card has an "Open Statement of Account" button.' },
            { label: 'Progress Invoicing & Deductions', desc: 'Set Billing Type = "Progress / Part-billing Invoice" to bill a percentage (Overall % or Per-line %). The Deductions block adds Retention, Advance Received, and Previous Payments — the PDF shows a clean "Final Payable" math block. Lines with qty=0 or price=0 are allowed for retention release invoices.' },
            { label: 'Duplicate Invoice', desc: 'Kebab menu on any invoice row → "Duplicate Invoice" clones it into a fresh Draft with a new INV-####. Perfect for recurring/monthly billings.' },
            { label: 'Customer TRN on Invoice PDFs', desc: 'Add the customer\'s VAT/TRN/GSTIN to their contact (Contacts → Edit → Tax ID / VAT TRN). It now prints under the Bill-To block on every Invoice / Quotation / Credit Note PDF automatically.' },
            { label: 'Customer Project Picker on Invoices', desc: 'Save projects on a contact (Contact → Projects tab) and they appear as a dropdown when raising an invoice. Selecting one auto-fills Project Reference + Project Location.' },
            { label: 'AI BOQ Upload', desc: 'Inside the Quotation create form, click "Upload BOQ" to drop in an Excel/CSV/Word/PDF/scanned image. AI extracts every line item, you preview & edit, then Append/Replace/Smart-Merge into the quote.' },
            { label: 'Stripe Subscription Billing', desc: 'Self-serve upgrade to Basic / Pro / Enterprise via Stripe Checkout. See Subscription & Billing.' }
          ]
        },
        {
          title: 'Navigation Overview',
          description: 'The sidebar contains all modules organized by category:',
          items: [
            { label: 'Dashboard', desc: 'Overview of your business KPIs' },
            { label: 'Modules', desc: 'HR, Payroll, Sales, Accounts, Inventory' },
            { label: 'Analytics', desc: 'Reports, Forecasting, Insights' },
            { label: 'Tools', desc: 'Tasks, Calendar, Documents, Notes (sticky notes), Topbar Calculator' },
            { label: 'Settings', desc: 'Company and user configuration' }
          ]
        }
      ]
    },
    {
      id: 'end-to-end-workflow',
      title: 'End-to-End Workflow',
      icon: Target,
      content: [
        {
          title: 'The BizCore Sales-to-Cash Flow (Illustrated)',
          description: 'Here is exactly how BizCore takes you from a fresh enquiry all the way to the final receipt — every step links to the next so nothing gets lost. Follow the numbered stages below.',
          steps: [
            '① QUOTATION & COSTING — In Sales → Quotations, click "New Quotation". Add the customer (inline + button creates a new contact on the fly), the scope and the line items. Open the Costing Sheet tab to enter your supplier/sub-contractor costs received via RFQ — the system computes the gross margin in real time so you know what you are bidding.',
            '② INTERNAL APPROVAL — Save as Draft → kebab menu → "Submit for Approval". Approvers see the quotation under Approvals → Pending. They can approve, request changes, or revise. Save as Revision creates QTN-#### (R1), (R2)… so the audit trail is preserved. Use Compare to diff any two revisions side-by-side.',
            '③ SEND TO CLIENT — Once internally approved, kebab → "Send to Client" / Download PDF / Share via WhatsApp / Email. The branded PDF carries your logo, TRN, signature stamps, and a QR code for verification.',
            '④ CLIENT APPROVAL → LPO / AGREEMENT — Capture the client\'s acceptance (LPO #, agreement reference, even verbal confirmation note) in the Quotation\'s "Client Acceptance" block. If it is an AMC / Service Contract, convert the quotation into an Agreement via Operations → Agreements → New Agreement (link the quotation).',
            '⑤ ISSUE LPO TO SUPPLIER / SUB-CONTRACTOR — Trade & Commerce → Purchases → New PO. Pick the supplier, add line items (carry over from your supplier quote received in step ①). Submit for approval. Once approved, the system creates the supplier ledger entry automatically.',
            '⑥ PROFORMA OR TAX INVOICE FOR ADVANCE — On the original quotation kebab → "Convert to Invoice". Pick Invoice Type = Proforma (no QR, amber "not a tax invoice" badge — use before agreement is signed) OR Tax Invoice (5% VAT, QR code). Add the advance % per your payment terms. Send the PDF. Receive the advance via Cash/Bank.',
            '⑦ JOB ORDER WITH SCOPE, CHECKLIST & T&Cs — Operations → Job Orders → New Job Order. Pick the customer (searchable dropdown), site, scope of works, equipment multi-select (AHU/FCU/Chiller/Generator/…), AI-generated checklist per equipment, Method of Statement and Risk Assessment (mark Optional if not required), payment terms snapshot, and the T&Cs. Save & Download PDF or share via WhatsApp.',
            '⑧ EXECUTE THE WORK ON SITE — Site team picks up the Job Order. As work progresses, status flows: Draft → Assigned → In Progress → Completed by Site → Approved.',
            '⑨ JOB COMPLETION / DELIVERY REPORT — Inside the Job Order, open the "Service Reports" tab (or use Reports → "Job Completion Report" / "Delivery Report"). Tick the checklist items, write the work-done narrative, list materials used, the people who attended, attach before/after photos and PDFs (any size — they embed inline in the final PDF), and capture the customer-rep\'s signature on the touch canvas. Download the branded PDF for on-site handover.',
            '⑩ INVOICE FOR PROGRESS / FINAL BILLING — Sales → Invoices → New Invoice (or kebab on the Quotation → "Generate Next Invoice"). Set Billing Type = "Progress / Part-billing Invoice" to bill a % of the total (Overall % OR Per-line %). The Deductions block applies Retention (before VAT), Advance Received, and Previously Paid amounts — the PDF prints a clean auditable "Final Payable" math block. Repeat per milestone until the final retention-release invoice.',
            '⑪ MARK AS PAID (with the correct Bank account) — When the customer payment lands, kebab → "Mark as Paid". A dialog asks for Payment Date, Payment Mode (Cash / Bank Transfer / Cheque / Card / Online) and the EXACT Bank or Cash account. BizCore auto-creates the Receipt Voucher (status = Approved), posts a balanced JE (DR Bank / CR Accounts Receivable), and the deposit shows up in your Bank Statement, Trial Balance, P&L and Dashboard immediately.',
            '⑫ SUPPLIER / SUB-CONTRACTOR PAYMENT — Trade & Commerce → Purchases → record the Supplier Bill (or upload supplier invoice via Attachments). Create a Payment Voucher from Vouchers → New Payment Voucher (or directly from the bill → "Pay Bill"). Manager approves; once approved, the JE posts (DR Accounts Payable / CR Bank) and the bank balance updates.',
            '⑬ RECONCILE & CLOSE — Open the Dashboard. If the system detects any drift between invoices, vouchers and the ledger (e.g. someone deleted a duplicate voucher), the RED Reconciliation banner appears. Click "Reconcile Now" — BizCore recomputes every invoice\'s paid amount, voids stale JEs, reverts invoices whose receipt vouchers were deleted, and creates any missing JEs. Green banner = books in sync.',
          ]
        },
        {
          title: 'Where each step lives in the app',
          description: 'Quick navigation cheat-sheet for the above flow:',
          items: [
            { label: '① Quotation & Costing', desc: 'Sales → Quotations → New Quotation → Costing Sheet tab' },
            { label: '② Internal Approval', desc: 'Approvals → Pending (auto-routed)' },
            { label: '③ Send to Client', desc: 'Quotation kebab → Send / Download PDF / WhatsApp / Email' },
            { label: '④ Client Acceptance', desc: 'Quotation → Client Acceptance block (LPO/Agreement #, date, notes)' },
            { label: '⑤ Supplier LPO / Sub-contractor PO', desc: 'Purchases → New PO' },
            { label: '⑥ Proforma / Tax Invoice (Advance)', desc: 'Quotation kebab → Convert to Invoice → Invoice Type dropdown' },
            { label: '⑦ Job Order', desc: 'Operations → Job Orders → New Job Order (4 tabs: Details / Checklist / MoS & Risk / Service Reports)' },
            { label: '⑨ Completion / Delivery Report', desc: 'Job Order → Service Reports tab OR Reports → New Report (Job Completion / Delivery Report layouts)' },
            { label: '⑩ Progress / Final Invoice', desc: 'Sales → Invoices → Billing Type = Progress / Part-billing' },
            { label: '⑪ Mark Paid with Bank Account', desc: 'Invoice row kebab → Mark as Paid → pick Payment Mode + Bank Account' },
            { label: '⑫ Supplier Payment', desc: 'Vouchers → New Payment Voucher (or Purchases → Pay Bill)' },
            { label: '⑬ Reconcile', desc: 'Dashboard → "Reconcile Now" banner (only appears when drift is detected)' },
          ]
        },
        {
          title: 'Tips for an audit-ready workflow',
          description: 'Best-practice habits that keep your books clean as you scale:',
          items: [
            { label: 'Always capture the LPO / agreement reference', desc: 'Even if verbal — type the date + verbal-confirmation note on the quotation\'s Client Acceptance block so the auditor sees the trail.' },
            { label: 'Use Revisions instead of editing-in-place', desc: 'Quotation kebab → "Save as Revision" preserves R0/R1/R2 versions. Audit-friendly.' },
            { label: 'Pick the correct Bank Account on every Mark-as-Paid', desc: 'The dialog now asks — never skip this. The deposit needs to hit the right ledger account for your bank reconciliation to work.' },
            { label: 'Generate the Service Report BEFORE the final invoice', desc: 'The Service Report is your evidence of completion. Without it the final invoice can be disputed.' },
            { label: 'Run "Reconcile Now" weekly', desc: 'Click the dashboard banner once a week. It catches silent drift and saves you hours at month-end close.' },
            { label: 'Help is always one click away', desc: 'The Help (?) icon in the topbar opens this User Guide from any screen — bookmark this workflow page.' },
          ]
        }
      ]
    },
    {
      id: 'dashboard',
      title: 'Dashboard',
      icon: House,
      content: [
        {
          title: 'Main Dashboard',
          description: 'Your central hub for business insights.',
          features: [
            'Financial summary cards (Revenue, Expenses, Profit)',
            'Receivables and Payables overview',
            'Recent transactions list',
            'Quick action buttons',
            'Compliance alerts widget'
          ]
        }
      ]
    },
    {
      id: 'hr-payroll',
      title: 'HR & Payroll',
      icon: Users,
      content: [
        {
          title: 'Employee Management',
          description: 'Manage your workforce efficiently. You can create departments and designations directly from the Add Employee form.',
          features: [
            'Add/Edit employee profiles with complete details',
            'Create departments inline when adding employees (click + button)',
            'Create designations inline when adding employees (click + button)',
            'Document uploads (ID, contracts, certificates)',
            'Leave balance tracking',
            'Employee search and filtering'
          ]
        },
        {
          title: 'Payroll Processing',
          description: 'Generate and manage payroll runs.',
          features: [
            'Monthly payroll generation',
            'Overtime calculation (Regular 1.25x, Weekend 1.5x, Holiday 2x)',
            'Deductions (Loans, Absences, Other)',
            'Earnings (Bonus, Commission, Leave encashment)',
            'Payslip PDF generation',
            'Approval workflow'
          ]
        },
        {
          title: 'End of Service (EOS)',
          description: 'Calculate employee termination benefits.',
          features: [
            'Configurable EOS formula with tier structure',
            'Automatic calculation based on service years',
            'Support for different termination types',
            'Detailed breakdown report'
          ]
        }
      ]
    },
    {
      id: 'accounts',
      title: 'Accounts & Finance',
      icon: CurrencyDollar,
      content: [
        {
          title: 'Chart of Accounts',
          description: 'Manage your accounting structure.',
          features: [
            'Five account types: Assets, Liabilities, Equity, Revenue, Expenses',
            'Hierarchical account organization',
            'Bank and Cash account tracking',
            'Account reconciliation'
          ]
        },
        {
          title: 'Journal Entries (Manual)',
          description: 'Record non-cash adjustments — accruals, prepayments, depreciation, opening balances, deferred income/expense, period closing entries.',
          features: [
            'Double-entry bookkeeping with auto Dr=Cr balance check',
            'Auto-incrementing entry number, shared with voucher-generated entries',
            'Attach supporting documents to each entry',
            'Posted entries flow into Trial Balance, P&L, Balance Sheet, Account Ledger',
            '⚠️ Cash/Bank movements are blocked here — they MUST go through Payment or Receipt Vouchers (voucher-centric workflow)',
          ]
        },
        {
          title: 'Voucher-Centric Workflow (How money is recorded)',
          description: 'BizCore enforces a Tally/Zoho-style voucher-centric accounting flow. Every cash or bank transaction flows through a numbered, approvable voucher — never a raw Journal Entry.',
          steps: [
            'Cash/Bank OUT → Create a Payment Voucher (Vouchers → New Payment Voucher)',
            'Cash/Bank IN  → Create a Receipt Voucher (Vouchers → New Receipt Voucher)',
            'Submit voucher → appears in central Approvals page (and on Approval History tab once actioned)',
            'Admin approves → System auto-posts a balanced Journal Entry, status = Approved',
            'Trial Balance, Account Ledger, P&L, Balance Sheet update instantly',
            'Manual / pre-printed voucher books? Enter the paper number in the "Manual / External Voucher #" field for audit reconciliation',
            'Need to correct an approved voucher? Admin clicks Amend → original is voided (JE reversed) and a fresh Draft clone is created — full audit trail preserved',
            'Pay an Invoice (Sales → Mark Paid) or Pay a Payroll (Payroll → Mark Paid) — system AUTO-creates the corresponding Receipt/Payment Voucher with synced numbering, no manual step',
          ]
        },
        {
          title: 'Financial Reports',
          description: 'Generate key financial statements. All financial & inventory reports now share a universal toolbar at the top (date range, search, View, PDF, Excel).',
          features: [
            'Trial Balance with running totals',
            'Profit & Loss Statement',
            'Balance Sheet',
            'Date range filtering',
            'Universal Report Toolbar — search filter + branded PDF + styled Excel export available on every tab (Trial Balance, P&L, Balance Sheet, Account Ledger, Stock Summary, Bin Cards, Transfers, Job Orders…)',
            'Exports honour the active date range and search filter',
            'Excel exports use openpyxl styled headers; PDFs use WeasyPrint with company logo, TRN and "Page X of Y" footers'
          ]
        },
        {
          title: 'Statement of Account (Receivables & Payables)',
          description: 'A complete customer / supplier ledger with aging, exportable as PDF / Excel / CSV. Find it at Finance & Accounts → Statement of Accounts, or click "Open Statement of Account" on any contact card.',
          features: [
            'Two tabs — Receivables (Customers) and Payables (Suppliers)',
            'Pick a party + date range; opening balance is auto-computed from transactions before the From date (Tally / Zoho style)',
            'KPI bar: Opening Balance, Total Invoiced/Billed, Tax in period, Total Received/Paid, Closing Balance',
            'Aging buckets: Current / 1-30 / 31-60 / 61-90 / 90+ days, computed on lifetime outstanding',
            'Full ledger: Date, Due Date, Type (Invoice/Receipt/Bill/Payment), Doc #, Project, Description, Tax, Total, Debit, Credit, Running Balance, Days Overdue, Aging badge',
            'When no party is picked you see an "All Customers / All Suppliers" summary table — sortable by balance, with Open buttons that drill into each party',
            'Exports: branded PDF (A4 landscape with company logo + TRN), Excel (.xlsx) with styled headers, CSV for spreadsheets',
            'Customer SoA includes Invoices + Receipt Vouchers. Supplier SoA includes Purchase Invoices + Payment Vouchers',
            'Tip — to email the statement to a customer today, click Download → PDF and attach it manually. SendGrid email integration is on the roadmap so you can one-click "Send Statement"'
          ]
        }
      ]
    },
    {
      id: 'sales',
      title: 'Sales & Invoicing',
      icon: ChartLineUp,
      content: [
        {
          title: 'Contact Management',
          description: 'Manage customers and suppliers — now with mandatory company name, Tax ID / VAT TRN, and saved Projects.',
          features: [
            'Customer/Supplier/Both contact types',
            'Complete contact details — Name, Company Name (mandatory), Email, Phone, Address, Country',
            'Tax ID / VAT TRN — accepts UAE TRN (15 digits), Indian GSTIN (15 chars), or any VAT number. Prints under the Bill-To block on every Invoice / Quotation PDF',
            'Projects tab on each contact — store reusable Project Name, Location, Reference. They appear as a dropdown when raising an Invoice / Quotation for this customer and auto-fill Project Reference + Project Location',
            'Transaction history per contact + "Open Statement of Account" button → jumps straight to the Receivables / Payables ledger for this party',
            'Inline contact creation from forms (click + next to Customer / Supplier on any document form)'
          ]
        },
        {
          title: 'Quotations',
          description: 'Create professional quotations with full approval workflow, costing sheets, AI BOQ upload, and revision tracking.',
          features: [
            'Line items now have: Description, Specification (brand/model/specs), Qty, Unit, Price, Tax %',
            'Unit dropdown — Nos / Mtr / Litre / Lot / Kg + ✏️ Custom… for sq.ft, kVA, m³, etc.',
            'Specification text renders as a small subtitle under the description on the PDF',
            'Project Reference and Project Location — show on lists, search filters, and on the PDF',
            'Overall Discount only — at the quotation level (% or fixed). Per-line discount has been removed',
            'Payment Terms (mandatory): preset list (Advance / Net 15/30/45/60 / COD / Credit) PLUS "✏️ Type custom…" for free-form like "30% advance, 60% on delivery, 10% retention 30 days"',
            'Quote Validity period (mandatory, default 30 days)',
            'Scope of Works (multi-line) and auto-numbered Exclusions list rendered on the PDF',
            'Image Attachments embedded inline as full pages inside the Quotation PDF',
            'Professional PDF with company logo, QR code, "Page X / Y" footer, and Prepared / Approved By stamps',
            'PDFs never split a single line item across two pages — pagination is automatic',
            'Resizable form: drag the bottom-right corner of the create/edit modal to make it bigger while typing',
            'Kebab (3-dot) action menu on each row: Edit, Duplicate, Save as Revision, Costing Sheet, Send, PDF, Delete',
            'Duplicate Quotation: one-click clone to a new Draft for fast re-quoting',
            'Save as Revision: edit an Approved/Sent quote and tick "Save as Revision" — system auto-bumps the revision tag (R0 → R1 → R2…) while keeping the same QTN-#### document number. Original revision is preserved (not overwritten).',
            'Show Revisions toggle on the Quotations list — when ON, all historical revisions are listed as separate rows tagged QTN-0001 (R1), (R2)… so you can open any previous version',
            'Revision Diff Viewer — select any two revisions of the same quotation and click "Compare". Added / removed / changed line items, total deltas, and header field changes are highlighted side-by-side in colour (green added, red removed, amber changed)'
          ]
        },
        {
          title: 'AI BOQ Upload (Bill of Quantities)',
          description: 'Drop a customer\'s BOQ file into the Quotation form and let AI extract the line items for you. Optional and safe — your manual entry path is unchanged.',
          steps: [
            'Open Sales → Quotations → click Create',
            'Click the "Upload BOQ" button next to "Add Line"',
            'Drag-and-drop or pick the file — supports Excel (.xlsx, .xls), CSV, Word (.docx), PDF, scanned PDF, JPG / PNG / WEBP. Up to 25 MB',
            'Excel/CSV is parsed locally (free, instant). PDF/Word/image goes through Gemini 3 Pro vision',
            'Edit the extracted rows in the preview table — change description, qty, unit, price, tax, or delete a row',
            'Pick a commit mode: Append (add below existing items) · Replace (clear and use only imported) · Smart Merge (combines duplicates by description)',
            'Click "Add N items to quotation" — rows drop into the line-items table. You can still edit/save normally'
          ]
        },
        {
          title: 'Quotation Costing Sheet (P&L)',
          description: 'Track project profitability against every quotation with a built-in costing module.',
          features: [
            'Open from the Quotation Kebab Menu → Costing Sheet',
            'Add cost lines: Material, Labour, Subcontractor, Overheads, Other',
            'Auto-calculates Total Cost, Gross Margin, Margin %, and Profit/Loss vs the quoted Sell Price',
            'Two PDF export modes: Internal (full cost breakdown) and Client (margin-hidden summary)',
            'Useful for budget approvals and post-project P&L review'
          ]
        },
        {
          title: 'Part Invoicing — flagship update this revision',
          description: 'Bill a job in slices. Two entry points that share the same base number with a numeric suffix (INV-XXXX-1, -2, -3 …) and full cumulative-% enforcement server-side.',
          features: [
            'Path A · Per Quotation — Sales → Quotations → row menu → "Create Part Invoice…". Enter Approved % per line and pick Apply Mode (Amount scales the line total, Qty scales the quantity). Tax is computed AFTER the % is applied. Advance vouchers on the quotation appear in a pool and can be applied per invoice',
            'Path B · Per Project — Invoices tab → "New Part Invoice (Project)" button. Chain identified by customer + Project Reference. Round 1: manually enter Original Qty + Original Rate + Approved Qty per line to establish the scope. Round 2+: anchor scope auto-loads with Already/Balance qty and only allows billing up to the balance',
            'Approved Qty ↔ Approved % stay in sync — edit either, the other auto-fills',
            'Original Qty/Rate is editable on later rounds with a visible "base was: X" note; deviations are captured on the invoice\'s part_deviation_notes audit trail. The balance ceiling stays anchored to round 1 to keep the scope contract stable',
            'Add Extra lines on a part invoice — items not on the quotation/original scope. Extra lines are excluded from cumulative-% tracking',
            'Autosave to localStorage (Project flow) — restore-draft banner on reopen, live "auto-saved" indicator, native beforeunload prompt catches accidental navigation',
            'Full accounting wiring — appears on GET /invoices with is_part_invoice + part_sequence + base_invoice_number metadata. Approve → balanced Journal Entry (DR AR total, CR Sales subtotal + Tax payable). Shows on the customer Statement of Account with correct debit / running balance',
            'Chain closes automatically when every line reaches 100 % cumulative — parent quotation flips to status "invoiced"; project chain marked fully-invoiced. Intermediate parts show status "part_invoiced" on the quotation',
          ]
        },
        {
          title: 'Quotation Workflow',
          description: 'Full lifecycle from draft to invoiced, with revision history.',
          steps: [
            'Create quotation (Draft)',
            'Submit for internal approval (Pending Approval)',
            'Manager approves or rejects (Approved / Rejected)',
            'Mark as Sent to Client (manual action)',
            'Client responds: Client Approved, On Hold, or Closed',
            'If client requests changes, edit the quote and tick "Save as Revision" — system auto-tags R1, R2, etc. and preserves the original',
            'Convert to Invoice (from Client Approved status)',
          ]
        },
        {
          title: 'Invoices',
          description: 'Generate and track invoices with billing types, project tracking, and quotation linking.',
          features: [
            'Automatic invoice numbering',
            'Invoice Type selector — Tax Invoice (VAT registered, QR on PDF), Proforma Invoice (amber "not a tax invoice" badge, no QR), or plain Invoice (no "TAX" wording). The PDF title and footer adapt automatically based on the selected type',
            'Link to Quotation — auto-fills customer, line items, project ref, and terms. Now shows ALL open quotes (was previously hidden when in Draft / Sent), sorted newest first, with status pill on each option',
            'Approval Guard — picking a non-approved quote in the Create Invoice form pops a confirmation: "Mark Client Approved & continue", "Mark Agreement Signed & continue", or "Continue anyway"',
            'Same Specification + Unit + Custom Unit + Project Reference / Location columns as Quotations',
            'Customer Project Picker — once a customer is selected, pick from their saved projects (managed on the Contact card) and Project Reference + Location auto-fill',
            'Customer TRN — fill the customer\'s Tax ID / VAT TRN on their contact; it prints under the Bill-To block on every Invoice / Quotation / Credit Note PDF automatically',
            'Duplicate Invoice — kebab menu → "Duplicate Invoice" clones the invoice into a fresh Draft with a new INV-#### number. Perfect for monthly billings or recurring charges',
            'VAT & GST category support (Standard, Zero-rated, Exempt)',
            'Bank details shown on PDF (from Settings)',
            'PDF with "Page X / Y" footer, Prepared/Approved By stamps, and the same anti-page-split layout as Quotations'
          ]
        },
        {
          title: 'Progress Invoicing & Deductions',
          description: 'Bill a percentage of a contract (construction / project work) and show retention, advance adjustments, and previously-paid amounts clearly on the invoice.',
          features: [
            'On the Create Invoice form, set Billing Type to "Progress / Part-billing Invoice" to unlock progress controls',
            'Progress Mode — Overall % (scales the whole contract value by a single percentage) or Per-line % (each line item gets its own "% complete")',
            'Overall % example: 30% of an AED 100,000 contract → invoice is automatically scaled to AED 30,000',
            'Deductions block: Retention (held back %), Advance Received (prior advance to adjust), Previous Payments (prior progress bills)',
            'Retention math reorder — retention is deducted BEFORE VAT (industry-standard contractor invoicing). Order on the PDF: Subtotal → less Retention → Net Taxable → +VAT (on net taxable) → Gross → less Advance → less Previous → Current Payable',
            'Example: 32,500 → less Retention 3,250 → Net Taxable 29,250 → +VAT 5% = 1,462.50 → Gross 30,712.50',
            'For progress invoices, lines may have qty = 0 or price = 0 (e.g. when raising a final retention release invoice)',
            'Together with the Statement of Account report, you get a clean ledger of every progress bill and where the retention sits today'
          ]
        },
        {
          title: 'Document Summary Bar (NEW)',
          description: 'Every Sales/Purchases/Vouchers/Inventory tab now shows an inline KPI bar above the list — see your numbers without opening a single document.',
          features: [
            'Inline KPI bar above the list: Count, Total Value, Outstanding (where applicable), This Month, Approved, Pending',
            'Always reflects the current search/filter applied to the list — change the search box and the bar updates',
            'Click "Summary" to open a side-drawer with deeper drill-down: status breakdown, payment-method split (vouchers), and Top 10 Counterparties by value',
            'Inventory drawer also shows Stock Value, Low Stock count, and Out of Stock count',
            'Eye icon → preview a branded PDF summary in a new tab',
            'PDF icon → download branded summary PDF (KPIs + status table + top counterparties)',
            'Excel icon → download styled .xlsx (Summary sheet + Documents sheet with all rows in the current filter)',
            'Available on: Invoices, Quotations, Purchase Orders, RFQs, GRNs, Payment Vouchers, Receipt Vouchers, Inventory Items'
          ]
        }
      ]
    },
    {
      id: 'inventory',
      title: 'Inventory (v2)',
      icon: Warehouse,
      content: [
        {
          title: 'Warehouses / Stores (NEW)',
          description: 'Inventory v2 introduces full multi-warehouse capability. A default "Main Store" is auto-created for every company; you can add any number of Branch / Site / Van warehouses on top.',
          features: [
            'Inventory → Warehouses tab → "Add Warehouse"',
            'Type: Main, Branch, Site, Vehicle/Van — used for filters and badges',
            'Address, in-charge person, contact phone, optional GPS',
            'Default warehouse can be set per company — used when no warehouse is specified on a transaction',
            'Soft delete supported (warehouse becomes Inactive but stock history is preserved)'
          ]
        },
        {
          title: 'Stock Transfers Between Warehouses (NEW)',
          description: 'Move stock from one warehouse to another with a fully audited transfer document — Tally / Zoho-style.',
          steps: [
            'Inventory → Transfers tab → "New Transfer"',
            'Pick the From and To warehouses (cannot be the same)',
            'Add items + quantity — system validates that the From warehouse has enough on-hand',
            'Optional reference number, date, and notes',
            'Submit → status moves Draft → In Transit',
            'Receiver clicks "Receive" → status moves to Completed; stock automatically deducts from origin and increments at destination',
            'Reverses cleanly if cancelled before receiving',
            'Every transfer is reflected in both warehouses\' Bin Card history'
          ]
        },
        {
          title: 'Bin Card (NEW)',
          description: 'Open any item card → "Bin Card" tab to see the full stock-in / stock-out ledger across all warehouses.',
          features: [
            'Per-warehouse opening balance, transactions, closing balance',
            'Transaction types: Purchase Receipt (GRN), Sales Issue (Invoice), Transfer In, Transfer Out, Stock Adjustment',
            'Date, Voucher/Document #, Quantity, Running Balance per warehouse',
            'Filter by date range and warehouse',
            'Export to PDF or Excel via the universal report toolbar'
          ]
        },
        {
          title: 'Stock Management Essentials',
          description: 'Core inventory functionality.',
          features: [
            'Product/Item master list with SKU, barcode, category, unit, price',
            'Stock value calculation (FIFO / Weighted Average)',
            'Low stock alerts per item and per warehouse',
            'Stock movement history (now warehouse-aware)',
            'Per-item Bin Card with full transaction trail',
            'Document Summary Bar for quick KPIs (Stock Value, Low Stock count, Out of Stock count)'
          ]
        },
        {
          title: 'Purchases',
          description: 'Manage procurement workflow. Create suppliers inline when creating POs.',
          features: [
            'Request for Quotation (RFQ)',
            'Purchase Orders (PO)',
            'Goods Receipt Notes (GRN) — receive into a specific warehouse',
            'Inline supplier creation (click + button)',
            'Purchase history tracking'
          ]
        }
      ]
    },
    {
      id: 'agreements',
      title: 'Agreements (AMC / SLA)',
      icon: Handshake,
      content: [
        {
          title: 'What is the Agreements module?',
          description: 'Operations → Agreements. End-to-end contract management for AMCs, Service Agreements, One-time Contracts, Project Contracts and SLAs. Covers everything from drafting to renewal, with AI assistance and branded PDFs.',
          features: [
            '7 agreement types — Comprehensive AMC, Non-Comprehensive AMC, Quarterly, Half-Yearly, One-time, Project, SLA',
            '7 trades (multi-select) — HVAC, Electrical, Plumbing, Fire-fighting, BMS, Generator, Solar',
            'Status flow: Draft → Pending → Active → Renewed / Expired / Terminated',
            'Branded PDF in 3 layouts (Classic / Modern Band / Minimalist) with company logo',
            'Search + filter by status, type, trade, expiring-within-60-days'
          ]
        },
        {
          title: 'Creating an agreement',
          description: '7-tab form for fast, structured drafting.',
          steps: [
            'Tab 1 (Details) — pick Type, multi-select Trades, pick Client from dropdown (auto-fills address/TRN/phone), set Start/End dates, Auto-renew + Escalation %, optionally add multiple Sites',
            'Tab 2 (Scope & Equipment) — select Equipment chips (AHU/FCU/Chiller/Pumps…), set Visit frequency + Emergency response time, click AI draft on Preamble and Scope of Work',
            'Tab 3 (Checklists) — Major + Minor service items per trade. Pre-seeded from templates OR generate with AI. Each row has Trade / Category / Description / Frequency, all editable',
            'Tab 4 (BOQ) — Bill of Quantities table (description / qty / unit / rate / auto-amount)',
            'Tab 5 (Commercials & Payment) — Annual + Total value, Currency, Payment schedule. Instalments are auto-built on save (4 quarterly, 2 half-yearly, 12 monthly, etc.)',
            'Tab 6 (T&C / Termination) — AI-drafted UAE-style numbered T&C + Termination clause + Special conditions',
            'Tab 7 (Sign-off) — Contractor + Client signatories with Name + Position + Company + Date'
          ]
        },
        {
          title: 'AI helpers (Gemini)',
          description: 'Four AI buttons available inside the form — uses Emergent LLM Key, no separate API key needed.',
          items: [
            { label: '✨ AI Preamble', desc: 'Generates a formal 2-3 paragraph opening recital with party intent + scope summary.' },
            { label: '✨ AI Scope of Work', desc: 'Detailed scope covering PPM activities by frequency, breakdown/emergency call-outs, deliverables, consumables, site safety, statutory compliance.' },
            { label: '✨ AI Checklist', desc: 'Generates 10+ items per trade split into Major (annual deep-clean) and Minor (monthly routine) — specific items like "Megger test cable insulation" not generic checks.' },
            { label: '✨ AI T&C + Termination', desc: 'UAE-style numbered clauses covering Payment & VAT, Spares, Service visits, Force majeure, Indemnity, Confidentiality, Dispute resolution + dedicated Termination clause (convenience / breach / insolvency / effects).' }
          ]
        },
        {
          title: 'Generating invoices from an agreement',
          description: 'Auto-build payment instalments and convert any of them into a real invoice in one click.',
          steps: [
            'Save the agreement → instalments are auto-generated in Tab 5 based on Payment schedule + period + total value',
            'Activate the agreement (Submit → Activate)',
            'When a payment is due, open the agreement → Tab 5 → click "Generate invoice" on that row',
            'A linked invoice (INV-####) is created in your Invoices module with the agreement number in the notes + linked_agreement_id',
            'The payment-term row updates to show the INV-#### badge instead of the button — re-generating on the same row is blocked',
            'Edit/Send/Receive the invoice normally from the Invoices module'
          ]
        },
        {
          title: 'Renewal & Duplicate',
          description: 'Two ways to multiply an agreement.',
          features: [
            'Duplicate (Copy icon) — exact carbon copy, new AGR-####, status Draft, dates retained, invoice tracking reset. Useful for onboarding similar clients with a boilerplate.',
            'Renew (Refresh icon) — only on Active / Expired / Terminated. Creates a new Draft with: (1) is_renewal_of pointing to the old one, (2) dates shifted forward by the original term, (3) Annual + Total value bumped by your saved Escalation %, (4) fresh payment instalments built. OLD agreement automatically marked Status = Renewed.',
            'Expiring filter — top filter bar → tick "Expiring (60d)" to see all agreements that need renewal action within 2 months.'
          ]
        }
      ]
    },
    {
      id: 'job-orders',
      title: 'Job Orders (Field Service)',
      icon: Briefcase,
      content: [
        {
          title: 'What is the Job Orders module?',
          description: 'Operations → Job Orders is BizCore\'s field service & maintenance work-order module — built for MEP / HVAC / facilities companies. It covers one-time jobs AND Planned Preventive Maintenance (PPM) with equipment-specific checklists, AI-drafted Method of Statement & Risk Assessment, before/after photos, signatures, and a branded PDF report.',
          features: [
            'Status flow: Draft → Assigned → In Progress → Completed by Site → Approved (Manager)',
            'Optional Cancel from Draft/Assigned/In Progress',
            'Service Type: one_time (single service call) or PPM (planned preventive)',
            'Priority: Low / Normal / High / Urgent',
            'Equipment multi-select: AHU, FAHU, FCU, Pumps – Chilled Water, Pumps – Booster, Electrical DB, Generator, Water Heater, Cooling Tower, VRV/VRF, Chiller — plus custom equipment types',
            'Link to Customer LPO number, Linked Invoice, Project Reference / Location',
            'Layout chooser: classic / modern_band / minimalist / compact (same PDF system as Invoices)'
          ]
        },
        {
          title: 'Creating a Job Order',
          description: 'Step-by-step creation flow.',
          steps: [
            'Operations → Job Orders → "New Job Order"',
            'Fill Customer Name, Site Location, Service Type, Scheduled Date, Priority',
            'Pick one or more Equipment chips (or add custom equipment via the + button)',
            'Optional — Customer LPO #, Link to an existing Invoice, Assigned Site Team',
            'Write the Scope of Work and Safety Notes (free text)',
            'Use AI Draft to auto-fill Method of Statement and Risk Assessment (Gemini), or type your own',
            'Add Checklist items manually OR pick an equipment from "⚡ AI add for equipment…" to auto-generate sections (Safety & PPE / Visual / Mechanical / Electrical / Performance / Cleaning)',
            'Click "Create" → status = Draft, JOB-#### is auto-assigned',
            'Re-open the order to upload Before / After pictures and Attachments',
          ]
        },
        {
          title: 'AI Helpers (Gemini)',
          description: 'BizCore uses Gemini (Emergent LLM key) to draft three different documents for you. You can always edit after generation.',
          items: [
            { label: '⚡ AI Checklist', desc: 'Pick an equipment from the dropdown inside the Checklist editor. Gemini returns 5–10 actionable items per section (Safety & PPE, Visual Inspection, Mechanical, Electrical, Performance/Readings, Cleaning) — adapted to one-time vs PPM service.' },
            { label: '✨ AI Method of Statement', desc: 'Click "AI draft" on the MoS panel. Generates Title, Purpose, Scope, Equipment & Tools list, PPE list, Step-by-Step procedure, Safety Controls and Emergency Procedures based on your Scope of Work + selected equipment + site location.' },
            { label: '⚠️ AI Risk Assessment', desc: 'Click "AI draft" on the RA panel. Generates 5+ hazards across mechanical / electrical / environmental / ergonomic / emergency categories with Likelihood (1-5), Severity (1-5), Risk Rating (Low / Medium / High) and concrete Control Measures.' }
          ]
        },
        {
          title: 'Before/After Pictures & Attachments',
          description: 'Job Orders include a built-in evidence trail for handover and warranty disputes.',
          features: [
            'Open the saved job → 3 upload boxes appear: Before Pictures, After Pictures, Attachments',
            'Drag-and-drop or pick a file — images embed as photos in the PDF; PDF/Word/Excel attachments appear in an appendix list',
            'Stored on disk AND replicated to MongoDB GridFS for stateless / production resilience',
            'Per-file metadata: filename, uploaded by, timestamp, MIME type, size',
            'Delete is admin-only and logged'
          ]
        },
        {
          title: 'Status Workflow',
          description: 'Job Orders enforce a clean 5-step lifecycle.',
          steps: [
            'Draft → editable by any creator. Add equipment, checklist, AI drafts, etc.',
            'Click "Assign" → set the site team / sub-contractor name → status = Assigned',
            'Site team clicks "Start" when arriving on-site → status = In Progress',
            'After the work, click "Mark Complete" → status = Completed by Site (site team\'s perspective)',
            'Manager / Supervisor reviews and clicks "Approve" → status = Approved (final). Audit trail captures who approved and when.',
            'A draft / assigned / in-progress order can be "Cancelled" if no longer needed — full audit kept'
          ]
        },
        {
          title: 'Job Order PDF',
          description: 'Branded one-click PDF report covering everything captured in the job.',
          features: [
            'Same 4 layouts as Invoices (Classic / Modern Band / Minimalist / Compact)',
            'Company logo, TRN, address, QR verification code',
            'Customer + Site, Service Type badge, LPO #, Scheduled Date',
            'Full equipment list and Scope of Work',
            'Method of Statement and Risk Assessment sections (if filled)',
            'Checklist grouped by section, with ✓ ticks and per-item comments',
            'Before / After photos arranged in a grid; other attachments listed in an appendix',
            'Status timeline (Created → Assigned → Started → Completed → Approved) with names and timestamps',
            'Signature blocks for Site Engineer and Client (handover stamps)',
            'Per-page footer with company name, job number, "Page X of Y"'
          ]
        }
      ]
    },
    {
      id: 'compliance',
      title: 'Compliance',
      icon: ShieldCheck,
      content: [
        {
          title: 'Compliance Center',
          description: 'Track licenses, visas, and renewals.',
          features: [
            'Multiple categories: License, Visa, Insurance, Contract, etc.',
            'Expiry date tracking',
            'Reminder days configuration',
            'Auto-status calculation (Active, Expiring Soon, Expired)',
            'Document attachments',
            'Dashboard widget with alerts'
          ]
        },
        {
          title: 'UAE Tax Compliance',
          description: 'VAT and Corporate Tax management.',
          features: [
            'VAT Settings (TRN, Filing frequency)',
            'VAT Return calculation (Form 201)',
            'Corporate Tax calculation (9% rate)',
            'Tax validation engine',
            'Filing deadline tracking'
          ]
        }
      ]
    },
    {
      id: 'analytics',
      title: 'Analytics & Reports',
      icon: ClipboardText,
      content: [
        {
          title: 'Analytics Dashboard',
          description: 'Visual insights into your business.',
          features: [
            'Revenue trends (Monthly/Quarterly)',
            'Expense breakdown by category',
            'Profit margin charts',
            'Receivables vs Payables aging'
          ]
        },
        {
          title: 'Forecasting',
          description: 'Predict future performance.',
          features: [
            'Revenue forecast (3/6/12 months)',
            'Expense projections',
            'Cash flow predictions',
            'Confidence indicators'
          ]
        },
        {
          title: 'Smart Insights',
          description: 'AI-powered recommendations.',
          features: [
            'Revenue decline/growth alerts',
            'High expense warnings',
            'Overdue receivables alerts',
            'Actionable recommendations'
          ]
        }
      ]
    },
    {
      id: 'tools',
      title: 'Tools & Utilities',
      icon: Lightning,
      content: [
        {
          title: 'Task Management',
          description: 'Kanban-style task board.',
          features: [
            'Create tasks with priority levels',
            'Drag-and-drop between columns',
            'Due date tracking',
            'Task assignment'
          ]
        },
        {
          title: 'Calendar',
          description: 'Manage events and reminders.',
          features: [
            'Monthly calendar view',
            'System events integration (Invoice due dates, Compliance expiry)',
            'Manual event creation',
            'PDC (Post-dated cheque) tracking'
          ]
        },
        {
          title: 'Document Storage',
          description: 'Centralized document repository.',
          features: [
            'File upload with categories',
            'Search by filename or tags',
            'Storage statistics',
            'Preview for images'
          ]
        },
        {
          title: 'Sticky Notes',
          description: 'Personal sticky-note workspace for quick reminders, action items, and meeting notes — scoped per user per company.',
          features: [
            'Open from sidebar → Notes',
            'Create unlimited notes with title and rich body text',
            'Pick from 6 sticky-note colours (Yellow, Pink, Blue, Green, Purple, Orange)',
            'Pin important notes to the top with a single click',
            'Quick search across title and content',
            'Auto-saved on edit; delete with confirmation',
            'Private to each user — your notes are not shared with other staff'
          ]
        },
        {
          title: 'Topbar Calculator',
          description: 'A built-in calculator launched from the Topbar — no need to switch apps while working on quotes or invoices.',
          features: [
            'Click the 🧮 Calculator icon in the Topbar to open the modal',
            'Standard arithmetic: + − × ÷, percentage, decimal',
            'Memory of last operation for chained calculations',
            'Keyboard shortcuts supported (number keys, Enter for =, Esc to close)',
            'Stays available across all pages — close and reopen any time'
          ]
        },
        {
          title: 'Quick Notes (Topbar 📝)',
          description: 'A personal scratchpad always one click away from the topbar — auto-saves to your account so it follows you between desktop and laptop.',
          features: [
            'Click the 📝 icon in the topbar (right next to the Calculator)',
            'Auto-saves about 700ms after you stop typing — no Save button needed',
            'Synced to your account in MongoDB; open it on another device and the notes are already there',
            'Works offline: keeps typing into a local cache and re-syncs automatically when the connection returns. The footer shows "offline · saved locally" when this happens',
            'Per-user-per-tenant isolation: your colleagues can\'t see your notes; switching to a different company gives you a separate notepad',
            'Status pill in the footer: ⏳ loading… / ☁ syncing… / ✓ synced / ⚠ offline · saved locally / Saved <timestamp>',
            'Copy-all and Clear-all buttons. Limit ~200,000 characters (~50 pages)',
            'Different from the sidebar "Sticky Notes" module — Quick Notes is one personal scratchpad; Sticky Notes is a multi-note board with colours and pinning'
          ]
        }
      ]
    },
    {
      id: 'settings',
      title: 'Settings',
      icon: Gear,
      content: [
        {
          title: 'Company Settings',
          description: 'Configure your company profile, including the TRN / Tax Registration No. that prints on every invoice.',
          features: [
            'Company name, address, and contact details',
            'Logo upload (appears on all PDFs — Quotations, Invoices, Reports)',
            'Auto-Heal: if the logo URL ever becomes stale, BizCore detects it and shows a warning on the Company Profile so you can re-upload — preventing broken logos in PDFs',
            'Tax & Online Identity card — TRN / Tax Registration No. (UAE TRN like 100123456700003 or Indian GSTIN like 22ABCDE1234F1Z5) and Website. Both auto-print in the header of every Invoice / Quotation / Delivery Note PDF',
            'Regional settings (Currency, Date format)',
            'Tax configuration (VAT, GST)',
            'Default terms & conditions for documents'
          ]
        },
        {
          title: 'Bank Details',
          description: 'Bank information displayed on invoices for client payments.',
          features: [
            'Bank Name and Branch',
            'Account Name and Account Number',
            'IBAN (International Bank Account Number)',
            'BIC / SWIFT Code',
            'Routing Code',
            'IFSC Code (for Indian banks)',
            'All filled fields appear automatically on Invoice PDFs'
          ]
        },
        {
          title: 'User Management',
          description: 'Manage users and permissions.',
          features: [
            '5 Role types: Super Admin, Admin, Manager, Staff, Viewer',
            'Permission matrix per module',
            'User creation by Admin/Super Admin',
            'Multi-company access (Accountant mode)',
            'Company Super Admin has full control'
          ]
        },
        {
          title: 'Backup & Audit',
          description: 'Data protection and compliance.',
          features: [
            'Manual backup creation',
            'Auto-backup scheduling',
            'Audit log viewer',
            'Cloud storage configuration'
          ]
        },
        {
          title: 'Email Integration Setup',
          description: 'Configure email to send POs, RFQs, and GRNs directly to suppliers. Each company sets up their own email credentials for full tenant isolation.',
          steps: [
            'Go to Settings > Email Integration',
            'Select your email provider: SendGrid, Resend, or SMTP',
            'For SendGrid: Get your API key from https://app.sendgrid.com/settings/api_keys (free tier: 100 emails/day)',
            'For Resend: Get your API key from https://resend.com/api-keys (free tier: 100 emails/day)',
            'For Gmail SMTP: Host = smtp.gmail.com, Port = 587, use an App Password (not your Gmail password) from https://myaccount.google.com/apppasswords',
            'For Outlook SMTP: Host = smtp-mail.outlook.com, Port = 587, Username = your email, Password = your password',
            'Enter your Sender Email (the "from" address recipients will see) and Sender Name',
            'Click "Save Settings" and then "Send Test Email" to verify',
            'Once verified, go to Purchases and click the green send icon on any PO/RFQ/GRN row to email the document with PDF attached'
          ]
        }
      ]
    },
    {
      id: 'billing',
      title: 'Subscription & Billing',
      icon: Star,
      content: [
        {
          title: 'Plans & Pricing',
          description: 'BizCore is billed per company on a monthly subscription. Choose the plan that fits your business size.',
          items: [
            { label: 'Free Trial', desc: 'Limited to 5 Quotations, 5 Invoices, and 5 Staff users — perfect to evaluate the platform before subscribing.' },
            { label: 'Basic — $39 / month', desc: 'Unlimited quotations & invoices, full Sales + Accounting modules, VAT/GST compliance, PDF branding, email support.' },
            { label: 'Pro — $79 / month', desc: 'Everything in Basic + Advanced Analytics, Bulk operations, Costing Sheets, Document Revisions, priority email support.' },
            { label: 'Enterprise — $199 / month', desc: 'Everything in Pro + Multi-Company / Accountant mode, Custom branding on PDFs, Risk analytics, dedicated support.' }
          ]
        },
        {
          title: 'How to Subscribe',
          description: 'Upgrade securely via Stripe Checkout. No card details are stored in BizCore.',
          steps: [
            'Open Billing from the sidebar (or click the "Upgrade" prompt in the trial banner)',
            'Select your plan: Basic, Pro, or Enterprise',
            'Click "Subscribe" — you will be redirected to a secure Stripe Checkout page',
            'Enter your card details and confirm payment',
            'On success, you are returned to BizCore and the plan activates immediately',
            'A Stripe receipt is emailed to you and the company\'s plan badge updates instantly'
          ]
        },
        {
          title: 'Trial Limits & Upgrades',
          description: 'During the Free Trial the system caps creation of certain records to encourage upgrade.',
          features: [
            'Trial cap: 5 Quotations, 5 Invoices, 5 Staff users',
            'When a cap is hit, the form will block save and prompt you to upgrade',
            'Upgrading lifts caps immediately — no need to re-enter data',
            'Cancel any time from the Billing page; you keep access until the period ends',
            'All historical data is preserved across plan changes'
          ]
        }
      ]
    },
    {
      id: 'platform-admin',
      title: 'Platform Administration',
      icon: Buildings,
      content: [
        {
          title: 'Adding a New Company',
          description: 'The Platform Admin can create new company accounts and assign a Super Admin who manages that company.',
          steps: [
            'Log in as Platform Admin (sgkapps.in@gmail.com)',
            'Navigate to Platform Admin from the sidebar',
            'Click the "Add Company" button (top right)',
            'Fill in Company Name, Country, Currency, Phone, and Plan',
            'Under "Company Super Admin", enter the admin\'s First Name, Last Name, Email, and Password',
            'Click "Create Company" — the company and its admin account are created instantly',
            'Share the login credentials (email + password) with the company Super Admin',
            'The new admin can immediately log in and start configuring their company'
          ]
        },
        {
          title: 'Managing Companies',
          description: 'Monitor and manage all registered companies from the Platform Admin dashboard.',
          features: [
            'View all companies with subscription status',
            'Filter by plan (Trial, Basic, Pro, Enterprise)',
            'Update subscription plans for any company',
            'View all registered users across companies',
            'Audit logs for platform-level actions',
            '"Clear Test Data" button to remove sample/demo companies'
          ]
        },
        {
          title: 'Emergency Password Reset (UI Tool)',
          description: 'Platform Admin → Users tab → click the "Reset Password" icon next to any user. The system generates a one-time temporary password, clears any failed-login lockout and login_attempts records, and forces a password change on next login.',
          steps: [
            'Log in as Platform Admin (sgkapps.in@gmail.com)',
            'Open Platform Admin → "Users" tab',
            'Find the locked-out / forgetful user. The status pill turns red if the account is locked',
            'Click the 🔑 "Reset Password" icon on that row',
            'A modal shows the auto-generated temporary password — click Copy and share it with the user (WhatsApp / email)',
            'The user logs in with the temp password — the system immediately prompts them to set a new permanent password (must_change_password is set)',
            'Behind the scenes: account_lockouts and login_attempts collections are cleared in the same call, so the user does NOT have to wait for the lockout timer to expire',
            'For self-service recovery (no admin needed) tell the user to use "Forgot Password" on the login page — see Account Recovery section above'
          ]
        },
        {
          title: 'View as User Mode',
          description: 'Platform Admin can preview the full user menu to inspect what company users see.',
          steps: [
            'Click "View as User" button in the sidebar',
            'The sidebar expands to show all user-facing modules (HR, Sales, Accounts, etc.)',
            'An amber "Viewing as User" banner appears at the top',
            'Click "Exit" to return to the clean Platform Admin view'
          ]
        }
      ]
    },
    {
      id: 'reports',
      title: 'Reports Preparation',
      icon: ClipboardText,
      content: [
        {
          title: 'Creating Business Reports',
          description: 'Prepare type-aware business reports — pick the type and the form auto-adapts to its industry-standard layout.',
          steps: [
            'Go to Reports from the sidebar (under Operations)',
            'Click "New Report"',
            'Select the Report Type — the form below dynamically reveals the right fields for that type',
            'Fill in the type-specific panel (amber for content, red for Action Required)',
            'Optionally upload Attachments (PDFs/images embed inline in the final PDF)',
            'Click "Create Report" — saved as Draft, then click Finalize when ready'
          ]
        },
        {
          title: 'Type-aware Report Layouts',
          description: 'Each report type unlocks its own purpose-built form. Listed below — fields appear only when relevant.',
          features: [
            'Job Completion Report — Project Reference / Location, Client, LPO Ref, Work Details, Confirmation Statement, Additional Notes, Client Signature pad, Signatory Name + Date',
            'Inspection Report — Inspection Type dropdown (Pre-handover / Quality / Safety / Final / Routine / Snag), Inspected By, Date + Time, Items Inspected, Findings (required), Overall Status (Passed / Passed-w-Observations / Failed / Re-Inspection)',
            'Daily Report — Weather, Manpower Count, Work Performed Today, Materials Received Today, Issues / Delays, Tomorrow\'s Planned Work',
            'Weekly Report — Period From → To, Week Label, Cumulative Progress %, Highlights, Completed, Pending, Risks & Mitigation, Next Week\'s Plan',
            'Monthly Report — Period From → To, Cumulative Progress %, Executive Summary, Financials (Invoiced / Received / Outstanding for the period), Highlights, Completed, Pending, Risks, Outlook for Next Month',
            'RFI (Request for Information) — Raised To, Query Subject, Reference Drawing / Spec, Detailed Question (required), Information Required By, Response / Response Date / Responded By (filled later when reply arrives)',
            'Incident Report — Incident Date + Time (required), Type, Severity badge (Minor / Moderate / Major / Critical), Location of Incident, People Involved, Description (required), Immediate Action Taken, Root Cause Analysis, Witnesses',
            'Universal Action Tracker (any non-Completion type) — Action Required + Action By + Target Closure Date',
            'Inline Attachments — JPG/PNG/GIF/WebP + PDFs render inline; multi-page PDFs expand into multiple pages',
            'Per-page Footer (all pages including attachments) — Company Name + Page X of Y + Report Number',
            'You can also add custom report types — click + next to the Report Type dropdown'
          ]
        },
        {
          title: 'Report Lifecycle',
          description: 'Reports follow a simple Draft to Final workflow.',
          features: [
            'Draft reports can be edited, deleted, and have files attached',
            'Click "Finalize" to mark a report as Final (read-only)',
            'Finalized reports cannot be edited or deleted',
            'Download PDF with company branding, QR code, and professional layout',
            'Upload attachments (photos, documents) up to 20MB each',
            'Custom report types can be added via the + button next to the type dropdown'
          ]
        }
      ]
    },
    {
      id: 'revisions',
      title: 'Document Revisions & Recycle Bin',
      icon: FileText,
      content: [
        {
          title: 'Revision System (R0, R1, R2...)',
          description: 'Every document starts as R0. When you edit it, a new revision (R1, R2, etc.) is created — the original is never overwritten.',
          features: [
            'Original document is saved as R0',
            'Each edit creates a new revision (R1, R2, R3...)',
            'All revisions are linked to the original document',
            'Document lists show only the latest revision',
            'Revision badge (e.g., R2) appears next to the document number',
            'Full revision history accessible via the workflow timeline'
          ]
        },
        {
          title: 'Recycle Bin',
          description: 'Deleted documents are soft-deleted and moved to the Recycle Bin.',
          features: [
            'Access Recycle Bin from the sidebar (under Administration)',
            'View all deleted documents across types (Quotations, Invoices, Reports, etc.)',
            'Filter by document type',
            'Admin can Restore documents (returns to Draft status)',
            'Admin can Permanently Delete documents (irreversible)',
            'Non-admin users see "Contact admin to restore"'
          ]
        }
      ]
    },
    {
      id: 'password-recovery',
      title: 'Account Recovery',
      icon: ShieldCheck,
      content: [
        {
          title: 'Setting Up Security Profile',
          description: 'Every user should set up their Date of Birth and Security Question for self-service recovery.',
          steps: [
            'Go to Settings in the sidebar',
            'Find the "Profile & Account Recovery" section',
            'Enter your Date of Birth (mandatory for recovery)',
            'Select a Security Question from the dropdown',
            'Enter your Security Answer (case insensitive)',
            'Click "Save Profile"'
          ]
        },
        {
          title: 'Forgot Password',
          description: 'Recover your password without needing admin help.',
          steps: [
            'On the login page, click "Forgot Password?"',
            'Enter your email address',
            'Enter your Date of Birth (must match your profile)',
            'Enter your Security Question Answer',
            'If all match, you can set a new password',
            'The reset link is valid for 15 minutes'
          ]
        },
        {
          title: 'Forgot Username (Email)',
          description: 'Recover your login email if forgotten.',
          steps: [
            'On the login page, click "Forgot Username?"',
            'Enter your First Name',
            'Enter your Date of Birth',
            'Enter your Security Question Answer',
            'If matched, your email will be displayed',
            'If recovery fails, contact info@sgkapps.com for assistance'
          ]
        }
      ]
    },
    {
      id: 'security',
      title: 'Security & Data Protection',
      icon: ShieldCheck,
      content: [
        {
          title: 'Role-Based Access Control',
          description: 'Manage custom roles with granular permissions.',
          features: [
            'Create custom roles (e.g., Sales Manager, Accounts Staff)',
            'Assign permissions per module (View, Create, Edit, Delete)',
            '10 modules supported: HR, Accounts, Sales, Settings, etc.',
            'Color-coded role badges',
            'System roles cannot be modified'
          ]
        },
        {
          title: 'Failed Login Protection',
          description: 'Automatic account lockout for security.',
          features: [
            'Configurable max failed login attempts (default: 5)',
            'Automatic account lockout after threshold',
            'Configurable lockout duration (5-1440 minutes)',
            'Admin can manually unlock accounts',
            'View login attempt history'
          ]
        },
        {
          title: 'Session Management',
          description: 'Monitor and control active sessions.',
          features: [
            'View all your active sessions (device, IP, last activity)',
            'Terminate individual sessions',
            'Sign out from all devices at once',
            'Admin view of all company sessions',
            'Configurable max concurrent sessions'
          ]
        },
        {
          title: 'Password Security',
          description: 'Strong password policies and reset options.',
          features: [
            'Configurable minimum password length',
            'Require special characters, numbers, uppercase',
            'Password expiry policy (days)',
            'Admin can reset user passwords',
            'Force password change on next login option'
          ]
        },
        {
          title: 'Security Dashboard',
          description: 'Monitor security status at a glance.',
          features: [
            'Total users and active sessions count',
            'Locked accounts summary',
            'Failed logins in last 24 hours',
            'Recent security events log',
            'Quick access to security settings'
          ]
        }
      ]
    },
    {
      id: 'tax-safety',
      title: 'Tax Safety Engine',
      icon: ShieldCheck,
      content: [
        {
          title: 'UAE VAT Compliance',
          description: 'Automated VAT validation and reporting.',
          features: [
            'Real-time VAT calculation on invoices',
            '4 VAT categories: Standard (5%), Zero-Rated, Exempt, Out of Scope',
            'Input/Output VAT tracking',
            'VAT Return preparation (Form 201)',
            'TRN (Tax Registration Number) validation'
          ]
        },
        {
          title: 'Corporate Tax Compliance',
          description: '9% UAE Corporate Tax calculation.',
          features: [
            'Automatic taxable income calculation',
            'Small business relief (under AED 375,000)',
            'Qualifying income identification',
            'Corporate tax filing deadline tracking',
            'Tax provision calculation'
          ]
        },
        {
          title: 'Tax Validation Engine',
          description: 'Automated compliance checks before filing.',
          features: [
            'Transaction validation against VAT rules',
            'Missing TRN detection',
            'Duplicate invoice warnings',
            'Zero-rated supply verification',
            'Compliance score calculation',
            'Fix recommendations for issues'
          ]
        }
      ]
    },
    {
      id: 'vouchers',
      title: 'Payment & Receipt Vouchers',
      icon: Receipt,
      content: [
        {
          title: 'Payment Vouchers',
          description: 'Record outgoing payments.',
          features: [
            'Create payment vouchers for cash, cheque, bank transfers',
            'Link to accounts for automatic journal entries',
            'Cheque number and date tracking',
            'Multi-line allocations',
            'Approval workflow (Draft → Pending → Approved)'
          ]
        },
        {
          title: 'Receipt Vouchers',
          description: 'Record incoming payments.',
          features: [
            'Create receipt vouchers for payments received',
            'Link to customer invoices',
            'Multiple payment methods supported',
            'Automatic journal entry creation on approval',
            'Print-ready voucher format'
          ]
        }
      ]
    }
  ];

  // BizCore Pro Guide Sections
  const proSections = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: Rocket,
      content: [
        {
          title: 'Welcome to BIZCore Pro™',
          description: 'BIZCore Pro™ is the professional multi-client management platform for accountants and accounting firms. It includes ALL standard BIZCore™ features (HR, Payroll, Sales, Purchases, Inventory, Financial Reports, Compliance) PLUS advanced multi-client management and UAE tax compliance tools.',
          steps: [
            'Register your accounting firm account',
            'Enable Accountant Mode in Settings',
            'Add your client companies with TRN details',
            'Access full ERP features for each client (Sales, HR, Payroll, etc.)',
            'Configure VAT and Corporate Tax settings per client',
            'Run pre-filing validation and submit returns'
          ]
        },
        {
          title: "What's New (Latest Update)",
          description: 'Recent additions you should explore — all available in every client workspace:',
          items: [
            { label: 'Job Orders Module (NEW)', desc: 'Operations → Job Orders. Field/site work-orders with equipment multi-select, AI-generated equipment checklists, AI Method of Statement & Risk Assessment (Gemini), before/after photos, signatures, 5-stage workflow (Draft → Assigned → In Progress → Completed by Site → Approved), and branded multi-layout PDFs. Available per client.' },
            { label: 'Inventory v2 — Warehouses & Bin Cards (NEW)', desc: 'Full multi-warehouse support per client. Add Branch / Site / Van warehouses, transfer stock between them with an audit trail, and view a per-item Bin Card showing every stock-in / stock-out movement.' },
            { label: 'Quotation Revisions + Diff Viewer (NEW)', desc: 'Save edits as new revisions (R0 → R1 → R2…) without changing the document number. Compare any two revisions side-by-side with the visual Diff Viewer — added / removed / changed lines and total deltas highlighted.' },
            { label: 'Universal Report Toolbar — PDF / Excel / Date Range / Search (NEW)', desc: 'Every financial and inventory report shares the same toolbar: pick a date range, type a search term, then View / PDF / Excel. Branded styled exports honour the current filter.' },
            { label: 'Platform Admin Password Reset Tool (NEW)', desc: 'Reset a locked-out client user from Platform Admin → Users with one click. Clears the lockout, generates a temp password, forces a password change on next login.' },
            { label: 'Document Summary Bar', desc: 'Inline KPI bar above every Sales / Purchases / Vouchers / Inventory list — Count, Total Value, Outstanding, This Month, Approved, Pending. Click Summary for status breakdown and Top 10 Counterparties. Export the summary as a branded PDF or styled Excel in one click. Always respects the current search/filter — extremely useful when reviewing a client at month-end.' },
            { label: 'Invoice Type Selector', desc: 'New "Invoice Type" dropdown on the Create/Edit Invoice form: Tax Invoice (QR + tax document), Proforma Invoice (amber "not a tax invoice" badge, no QR), or plain Invoice (no "TAX" wording). PDF title and footer adapt automatically.' },
            { label: 'Retention Math Reorder', desc: 'Retention now deducts BEFORE VAT on contractor invoices (industry standard). PDF math is now: Subtotal → less Retention → Net Taxable → +VAT → Gross → less Advance/Previous → Current Payable.' },
            { label: 'Type-aware Report Layouts', desc: 'Reports module now swaps form layout by Report Type — Inspection, Daily, Weekly, Monthly, RFI, Incident, Job Completion. Each gets its own purpose-built fields and PDF section. Universal "Action Required" tracker on every non-Completion type.' },
            { label: 'Inline Report Attachments', desc: 'Attach JPG/PNG/GIF/WebP/PDF to any report — images embed inline as full pages, multi-page PDFs expand into multiple captioned pages, and "Page X of Y" footer counts attachment pages.' },
            { label: 'Statement of Account', desc: 'Finance & Accounts → Statement of Accounts. Customer or supplier ledger with opening balance, running balance, aging buckets and branded PDF / Excel / CSV export. Per-client deep link from the contact card.' },
            { label: 'Progress Invoicing & Duplicate', desc: 'Billing Type = Progress invoices the contract by % (Overall or Per-line) with Retention / Advance / Previous-Payment deductions. Kebab → Duplicate Invoice clones into a fresh draft INV-#### in one click.' }
          ]
        },
        {
          title: 'Full Feature Access',
          description: 'BIZCore Pro™ includes all standard features for each client:',
          features: [
            'HR & Payroll Management - Employee records, payroll processing, EOS',
            'Sales & Invoicing - Quotations, invoices, contacts, payments',
            'Purchases & Inventory - POs, GRNs, stock management',
            'Accounts & Finance - Chart of accounts, journal entries, bank reconciliation',
            'Financial Reports - Trial Balance, P&L, Balance Sheet',
            'Compliance Center - Licenses, visas, renewals tracking',
            'Tasks & Calendar - Task management, event scheduling',
            'Document Storage - Centralized file management'
          ]
        },
        {
          title: 'Accountant Mode',
          description: 'To enable BIZCore Pro™ features:',
          items: [
            { label: 'Navigate to Settings', desc: 'Click Settings in the sidebar' },
            { label: 'Find Accountant Mode', desc: 'Scroll to Accountant Mode section' },
            { label: 'Enable the Toggle', desc: 'Turn on "Enable Accountant Mode"' },
            { label: 'Select Plan', desc: 'Choose Basic, Pro, or Enterprise' },
            { label: 'Access Dashboard', desc: 'Click "Accountant Dashboard" in sidebar' }
          ]
        }
      ]
    },
    {
      id: 'dashboard',
      title: 'Accountant Dashboard',
      icon: House,
      content: [
        {
          title: 'Multi-Client Overview',
          description: 'Your central hub for managing all client companies.',
          features: [
            'Total Clients count',
            'VAT Due amount across all clients',
            'Returns Ready for filing',
            'Compliance Alerts summary',
            'Client overview table with status indicators'
          ]
        },
        {
          title: 'Action Center',
          description: 'Quick access to important tasks.',
          features: [
            'Upcoming Deadlines (next 30 days)',
            'Alerts & Tasks requiring attention',
            'Quick Actions (Generate Reports, Run Validation)',
            'Document access across clients'
          ]
        }
      ]
    },
    {
      id: 'client-management',
      title: 'Client Management',
      icon: Buildings,
      content: [
        {
          title: 'Adding Clients',
          description: 'Create and manage client company profiles.',
          features: [
            'Company name and TRN (Tax Registration Number)',
            'Industry classification',
            'Contact information (email, phone, address)',
            'VAT registration status',
            'Free Zone entity indicator'
          ]
        },
        {
          title: 'Client Limits by Plan',
          description: 'Number of clients depends on your subscription:',
          items: [
            { label: 'Basic Plan', desc: 'Up to 10 clients' },
            { label: 'Pro Plan', desc: 'Up to 25 clients + bulk operations' },
            { label: 'Enterprise Plan', desc: 'Unlimited clients + advanced analytics' }
          ]
        }
      ]
    },
    {
      id: 'uae-tax-overview',
      title: 'UAE Tax Overview',
      icon: Receipt,
      content: [
        {
          title: 'UAE Tax Framework',
          description: 'Understanding the UAE tax system for businesses.',
          items: [
            { label: 'VAT (Value Added Tax)', desc: 'Introduced in 2018 at 5% standard rate. Businesses with taxable supplies exceeding AED 375,000 must register.' },
            { label: 'Corporate Tax', desc: 'Introduced in 2023 at 9% for profits above AED 375,000. Free Zone entities may qualify for 0% rate.' },
            { label: 'TRN (Tax Registration Number)', desc: 'Unique 15-digit identifier issued by FTA for all registered businesses.' },
            { label: 'FTA Portal', desc: 'Federal Tax Authority online portal for submissions and payments.' }
          ]
        },
        {
          title: 'Filing Frequencies',
          description: 'VAT return filing schedules:',
          items: [
            { label: 'Monthly Filing', desc: 'For businesses with annual taxable supplies > AED 150 million' },
            { label: 'Quarterly Filing', desc: 'Standard filing period for most businesses' },
            { label: 'Filing Deadline', desc: '28th day of the month following the tax period end' },
            { label: 'Payment Deadline', desc: 'Same as filing deadline - 28th of the following month' }
          ]
        },
        {
          title: 'Free Zone Benefits',
          description: 'Special tax treatment for Free Zone entities:',
          features: [
            '0% Corporate Tax rate for qualifying activities',
            'Must maintain adequate substance in the UAE',
            'Cannot conduct business with mainland UAE',
            'Separate books and financial statements required',
            'Annual compliance reporting to FTA'
          ]
        }
      ]
    },
    {
      id: 'vat-management',
      title: 'VAT Management',
      icon: Receipt,
      content: [
        {
          title: 'VAT Settings Configuration',
          description: 'Configure VAT settings per client company.',
          features: [
            'TRN (Tax Registration Number) - 15-digit FTA identifier',
            'VAT Registration Status toggle',
            'Filing Frequency (Monthly or Quarterly)',
            'Free Zone Entity indicator for 0% qualification',
            'Default VAT Category for transactions'
          ]
        },
        {
          title: 'VAT Return Calculation (Form 201)',
          description: 'Automatic VAT return generation compatible with FTA Form 201.',
          features: [
            'Box 1: Standard Rated Sales (5%)',
            'Box 2: Tax-exempt and Zero-rated Sales',
            'Box 3: Output VAT amount (Sales × 5%)',
            'Box 4: Standard Rated Purchases',
            'Box 5: Input VAT recoverable (Purchases × 5%)',
            'Box 6: Net VAT Payable/Refundable',
            'Automatic adjustments for corrections'
          ]
        },
        {
          title: 'VAT Categories',
          description: 'Supported VAT treatment types for transactions:',
          items: [
            { label: 'Standard Rated (5%)', desc: 'Default for most goods and services in UAE mainland' },
            { label: 'Zero Rated (0%)', desc: 'Exports, international transport, first sale of residential property, healthcare/education' },
            { label: 'Exempt', desc: 'Financial services, bare land, local passenger transport, life insurance' },
            { label: 'Out of Scope', desc: 'Salary payments, dividends, transfers between related parties' },
            { label: 'Reverse Charge', desc: 'Import of services from outside UAE - buyer accounts for VAT' }
          ]
        },
        {
          title: 'VAT Transactions Report',
          description: 'Detailed transaction listing for VAT periods.',
          features: [
            'Filter by date range (VAT period)',
            'Sales transactions with output VAT',
            'Purchase transactions with input VAT',
            'Export to Excel for FTA audit support',
            'Invoice number, customer/supplier, amount, VAT breakdown'
          ]
        }
      ]
    },
    {
      id: 'corporate-tax',
      title: 'Corporate Tax',
      icon: CurrencyDollar,
      content: [
        {
          title: 'UAE Corporate Tax Overview',
          description: 'Corporate Tax introduced June 2023 for financial years starting on/after this date.',
          items: [
            { label: 'Tax Rate', desc: '0% on taxable income up to AED 375,000, 9% on income above threshold' },
            { label: 'Qualifying Free Zone', desc: '0% rate available for qualifying income from Free Zone entities' },
            { label: 'Fiscal Year', desc: 'Aligned with company financial year (typically Jan-Dec or Apr-Mar)' },
            { label: 'Filing Deadline', desc: '9 months after the end of the relevant Tax Period' }
          ]
        },
        {
          title: 'Taxable Income Calculation',
          description: 'How BIZCore Pro calculates Corporate Tax liability.',
          features: [
            'Total Revenue from P&L statement',
            'Less: Allowable business expenses',
            'Less: Exempt income (dividends, capital gains)',
            'Equals: Taxable Income',
            'Apply threshold: First AED 375,000 at 0%',
            'Remaining income at 9% rate'
          ]
        },
        {
          title: 'Small Business Relief',
          description: 'Simplified regime for small businesses.',
          features: [
            'Revenue threshold: Below AED 3 million',
            'Elect to be treated as having no taxable income',
            'Simplified compliance requirements',
            'No transfer pricing documentation needed',
            'Must still register and file returns'
          ]
        },
        {
          title: 'Required Records',
          description: 'Documentation for Corporate Tax compliance.',
          features: [
            'Financial statements (audited if required)',
            'Tax computation workpapers',
            'Transfer pricing documentation',
            'Supporting invoices and contracts',
            'Bank statements and payment records'
          ]
        }
      ]
    },
    {
      id: 'tax-validation',
      title: 'Tax Compliance Engine',
      icon: ShieldCheck,
      content: [
        {
          title: 'Pre-Filing Validation',
          description: 'Comprehensive validation checks before filing to FTA.',
          features: [
            '8 VAT validation rules for Form 201 compliance',
            '7 Corporate Tax validation rules',
            'Error severity levels (Error, Warning, Info)',
            'Smart fix suggestions with direct navigation links',
            'Acknowledge warnings checkbox before proceeding',
            'Compliance disclaimer modal'
          ]
        },
        {
          title: 'VAT Validation Checks (8 Rules)',
          description: 'Automatic checks run before VAT filing:',
          items: [
            { label: '1. TRN Verification', desc: 'Valid 15-digit TRN format required' },
            { label: '2. Transaction Existence', desc: 'At least one taxable transaction in period' },
            { label: '3. Zero Value Invoices', desc: 'Warning for AED 0 value invoices' },
            { label: '4. Future Date Check', desc: 'No transactions dated after period end' },
            { label: '5. VAT Rate Validation', desc: 'Correct rates applied per category (5%/0%)' },
            { label: '6. Input VAT Balance', desc: 'Input VAT typically should not exceed output' },
            { label: '7. Missing Supplier TRN', desc: 'Warning for purchases without supplier TRN' },
            { label: '8. Large Transaction Review', desc: 'Flag unusual large-value transactions' }
          ]
        },
        {
          title: 'Corporate Tax Validation (7 Rules)',
          description: 'Enterprise plan - checks for CT filing:',
          items: [
            { label: '1. Fiscal Year Setup', desc: 'Valid fiscal year dates configured' },
            { label: '2. Revenue Threshold', desc: 'Check if below AED 3M for small business relief' },
            { label: '3. Expense Classification', desc: 'Non-deductible expenses identified' },
            { label: '4. Related Party Transactions', desc: 'Transfer pricing documentation check' },
            { label: '5. Free Zone Qualification', desc: 'Substance requirements for 0% rate' },
            { label: '6. Loss Carry Forward', desc: 'Validate loss utilization rules' },
            { label: '7. Filing Deadline', desc: 'Check submission within 9 months' }
          ]
        },
        {
          title: 'Filing & Period Lock',
          description: 'Secure the filed period to prevent modifications.',
          features: [
            'Compliance disclaimer acknowledgment required',
            'Period lock automatically applied after filing',
            'Locked periods cannot be edited (audit protection)',
            'Complete audit trail for all filings',
            'Filed returns history with timestamps',
            'Download filed return summary PDF'
          ]
        }
      ]
    },
    {
      id: 'bulk-operations',
      title: 'Bulk Operations',
      icon: Lightning,
      content: [
        {
          title: 'Bulk Reports',
          description: 'Generate reports for multiple clients (Pro+ plans).',
          features: [
            'VAT summary report across all clients',
            'Compliance overview report',
            'Batch PDF generation',
            'Export to Excel'
          ]
        },
        {
          title: 'Bulk Validation',
          description: 'Validate all clients at once.',
          features: [
            'Run validation for all clients',
            'Summary of passed/failed checks',
            'Prioritized action list',
            'Deadline compliance check'
          ]
        }
      ]
    },
    {
      id: 'deadlines',
      title: 'Deadline Tracking',
      icon: CalendarCheck,
      content: [
        {
          title: 'Filing Deadlines',
          description: 'Never miss a filing deadline.',
          features: [
            'Create deadlines per client',
            'VAT and Corporate Tax deadline types',
            'Calendar integration',
            'Upcoming deadlines view (60 days)',
            'Overdue alert notifications'
          ]
        },
        {
          title: 'UAE Filing Schedule',
          description: 'Standard UAE tax deadlines:',
          items: [
            { label: 'Monthly VAT', desc: '28th of following month' },
            { label: 'Quarterly VAT', desc: '28th after quarter end' },
            { label: 'Corporate Tax', desc: '9 months after year end' }
          ]
        }
      ]
    },
    {
      id: 'analytics',
      title: 'Enterprise Analytics',
      icon: Target,
      content: [
        {
          title: 'Risk Score (Enterprise)',
          description: 'Compliance risk assessment per client.',
          features: [
            'Score 0-100 (lower is better)',
            'Risk factors breakdown',
            'Historical trend analysis',
            'Benchmark against peers'
          ]
        },
        {
          title: 'Compliance Health',
          description: 'Overall compliance status.',
          features: [
            'Filings on-time percentage',
            'Outstanding issues count',
            'Last filing date tracking',
            'Improvement recommendations'
          ]
        },
        {
          title: 'Trend Analysis (Pro+)',
          description: 'VAT trends over time.',
          features: [
            'Output VAT trend',
            'Input VAT trend',
            'Net VAT payable trend',
            'YoY comparison'
          ]
        }
      ]
    },
    {
      id: 'plans',
      title: 'Subscription Plans',
      icon: Star,
      content: [
        {
          title: 'Plan Comparison',
          description: 'Choose the right plan for your firm.',
          items: [
            { 
              label: 'Basic', 
              desc: '10 clients, VAT management, Deadline tracking, Document storage' 
            },
            { 
              label: 'Pro', 
              desc: '25 clients, Bulk operations, Trend analysis, Priority support' 
            },
            { 
              label: 'Enterprise', 
              desc: 'Unlimited clients, Risk analytics, Custom branding, Dedicated support' 
            }
          ]
        }
      ]
    },
    {
      id: 'platform-admin-pro',
      title: 'Platform Administration',
      icon: Buildings,
      content: [
        {
          title: 'Adding a New Company',
          description: 'The Platform Admin can create new company accounts and assign a Super Admin.',
          steps: [
            'Log in as Platform Admin (sgkapps.in@gmail.com)',
            'Navigate to Platform Admin from the sidebar',
            'Click the "Add Company" button (top right)',
            'Fill in Company Name, Country, Currency, Phone, and Plan',
            'Under "Company Super Admin", enter the admin\'s First Name, Last Name, Email, and Password',
            'Click "Create Company" — the company and admin are created instantly',
            'Share the login credentials with the company Super Admin'
          ]
        },
        {
          title: 'Managing Companies & View as User',
          description: 'Monitor companies, clear test data, and inspect user menus.',
          features: [
            'View all companies with subscription status',
            'Update subscription plans for any company',
            '"Clear Test Data" button to remove sample/demo companies',
            '"View as User" mode to preview the user-facing menu',
            'Emergency password reset for any user',
            'Audit logs for platform-level actions'
          ]
        }
      ]
    },
    {
      id: 'reports-pro',
      title: 'Reports Preparation',
      icon: ClipboardText,
      content: [
        {
          title: 'Creating Business Reports',
          description: 'Prepare Job Completion, Inspection, RFI, Daily/Monthly Reports, and custom types.',
          features: [
            '12 default report types + custom types',
            'Fields: Subject, Date, Reference, Addressed To, Location, Priority',
            'Multi-line Details and Remarks fields',
            'File attachments (up to 20MB each)',
            'Draft to Final workflow',
            'Professional PDF with company branding and QR code'
          ]
        }
      ]
    },
    {
      id: 'revisions-pro',
      title: 'Document Revisions & Recycle Bin',
      icon: FileText,
      content: [
        {
          title: 'Revision System (R0, R1, R2...)',
          description: 'Every edit creates a new revision — originals are never overwritten.',
          features: [
            'Original = R0, each edit = R1, R2, R3...',
            'All revisions linked to original document',
            'Document lists show only latest revision',
            'Revision badge shown next to document number',
            'Full revision history in workflow timeline'
          ]
        },
        {
          title: 'Recycle Bin',
          description: 'Deleted documents are recoverable by admins.',
          features: [
            'Soft-delete moves to Recycle Bin',
            'Admin can Restore (back to Draft) or Permanently Delete',
            'Filter by document type',
            'Non-admins see "Contact admin to restore"'
          ]
        }
      ]
    },
    {
      id: 'password-recovery-pro',
      title: 'Account Recovery',
      icon: ShieldCheck,
      content: [
        {
          title: 'Self-Service Recovery',
          description: 'Users can recover passwords and usernames without admin help.',
          features: [
            'Set up Date of Birth + Security Question in Settings',
            '"Forgot Password?" — verify DOB + answer → set new password',
            '"Forgot Username?" — verify name + DOB + answer → shows email',
            'If recovery fails, contact info@sgkapps.com'
          ]
        }
      ]
    }
  ];

  // End-to-End Workflow section is universal — make it available in BOTH
  // standard and accountant modes so the Help button always lands on a valid
  // section.
  const workflowSection = standardSections.find(s => s.id === 'end-to-end-workflow');
  const sections = accountantMode
    ? (workflowSection ? [workflowSection, ...proSections] : proSections)
    : standardSections;

  // Filter sections based on search
  const filteredSections = sections.filter(section => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    if (section.title.toLowerCase().includes(query)) return true;
    return section.content.some(item => 
      item.title.toLowerCase().includes(query) ||
      item.description?.toLowerCase().includes(query)
    );
  });

  const activeContent = sections.find(s => s.id === activeSection);

  return (
    <div className="space-y-6" data-testid="user-guide-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              accountantMode ? 'bg-[#d4a84b]' : 'bg-[#1e3a5f] dark:bg-[#2d4a6f]'
            }`}>
              <Book size={24} className={accountantMode ? "text-[#0f172a]" : "text-[#d4a84b]"} weight="duotone" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                {accountantMode ? 'BIZCore Pro™ User Guide' : 'BIZCore™ User Guide'}
              </h1>
              <p className="text-muted-foreground text-sm">
                {accountantMode 
                  ? 'Multi-Client Business & Tax Compliance Platform' 
                  : 'Your Core Business & Compliance Partner'}
              </p>
              <div className="mt-1.5 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-[#0f172a] text-[#d4a84b] text-[11px] font-mono font-semibold dark:bg-[#d4a84b] dark:text-[#0f172a]" data-testid="user-guide-revision-badge">
                Revision {APP_REVISION.revisionCode} · Released {APP_REVISION.releasedOn}
              </div>
            </div>
          </div>
        </div>

        {/* Mode Badge */}
        {accountantMode && (
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#d4a84b] text-[#0f172a]">
            <Briefcase size={16} weight="fill" />
            <span className="text-sm font-semibold">Pro Edition</span>
          </div>
        )}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={20} />
        <input
          type="text"
          placeholder="Search guide..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          data-testid="guide-search-input"
        />
      </div>

      {/* QR Code — links to public User Guide PDF (Feb 2026) */}
      <UserGuideQRCard />

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar Navigation */}
        <div className="lg:w-64 flex-shrink-0">
          <nav className="bg-card rounded-xl border border-border p-2 sticky top-6">
            {filteredSections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                  activeSection === section.id
                    ? 'bg-[#d4a84b] text-[#0f172a]'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                }`}
                data-testid={`guide-nav-${section.id}`}
              >
                <section.icon size={18} weight={activeSection === section.id ? 'fill' : 'regular'} />
                <span className="text-sm font-medium">{section.title}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Content Area */}
        <div className="flex-1 min-w-0">
          {activeContent && (
            <div className="space-y-6">
              {/* Section Header */}
              <div className="flex items-center gap-3 p-4 rounded-xl bg-accent/50">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  accountantMode ? 'bg-[#d4a84b]' : 'bg-[#1e3a5f] dark:bg-[#2d4a6f]'
                }`}>
                  <activeContent.icon size={22} className={accountantMode ? "text-[#0f172a]" : "text-[#d4a84b]"} weight="duotone" />
                </div>
                <h2 className="text-xl font-bold text-foreground">
                  {activeContent.title}
                </h2>
              </div>

              {/* Content Cards */}
              {activeContent.content.map((item, index) => (
                <div 
                  key={index}
                  className="bg-card rounded-xl border border-border p-6 space-y-4"
                >
                  <h3 className="text-lg font-semibold text-foreground">
                    {item.title}
                  </h3>
                  
                  <p className="text-muted-foreground">{item.description}</p>

                  {/* Steps List */}
                  {item.steps && (
                    <ol className="space-y-2 mt-4">
                      {item.steps.map((step, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <span className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold bg-[#d4a84b] text-[#0f172a]">
                            {idx + 1}
                          </span>
                          <span className="text-foreground pt-0.5">{step}</span>
                        </li>
                      ))}
                    </ol>
                  )}

                  {/* Features List */}
                  {item.features && (
                    <ul className="space-y-2 mt-4">
                      {item.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle 
                            size={18} 
                            className="flex-shrink-0 mt-0.5 text-[#d4a84b]" 
                            weight="fill" 
                          />
                          <span className="text-foreground">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Items List (with label/desc) */}
                  {item.items && (
                    <div className="space-y-3 mt-4">
                      {item.items.map((listItem, idx) => (
                        <div 
                          key={idx} 
                          className="flex items-start gap-3 p-3 rounded-lg bg-accent/50"
                        >
                          <CaretRight 
                            size={18} 
                            className="flex-shrink-0 mt-0.5 text-[#d4a84b]" 
                            weight="bold" 
                          />
                          <div>
                            <span className="font-semibold text-foreground">
                              {listItem.label}
                            </span>
                            <span className="text-muted-foreground"> - {listItem.desc}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {/* Next Section Link */}
              {filteredSections.findIndex(s => s.id === activeSection) < filteredSections.length - 1 && (
                <button
                  onClick={() => {
                    const currentIdx = filteredSections.findIndex(s => s.id === activeSection);
                    if (currentIdx < filteredSections.length - 1) {
                      setActiveSection(filteredSections[currentIdx + 1].id);
                    }
                  }}
                  className="w-full flex items-center justify-between p-4 rounded-xl border-2 border-dashed transition-colors border-border hover:border-[#d4a84b]/50 text-foreground hover:text-[#d4a84b]"
                >
                  <span className="font-medium">
                    Next: {filteredSections[filteredSections.findIndex(s => s.id === activeSection) + 1]?.title}
                  </span>
                  <ArrowRight size={20} />
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="text-center py-6 border-t border-border">
        <p className="text-muted-foreground text-sm">
          {accountantMode ? 'BIZCore Pro™' : 'BIZCore™'} by{' '}
          <span className="font-semibold text-foreground">SGK</span>
          <span className="font-semibold text-[#d4a84b]"> Apps</span>
          {' '}| <a href="https://bizcore.sgkapps.com" target="_blank" rel="noopener noreferrer" className="hover:text-[#d4a84b] transition-colors">bizcore.sgkapps.com</a>
        </p>
      </div>
    </div>
  );
}
